import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, MessageSquare } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  assignFreightContainer,
  getFreightiq,
  saveFreightBooking,
  selectFreightCarrier,
  sendFreightiqMessage,
  updateFreightEta,
  updateFreightiq,
  updateFreightStatus,
} from "../lib/api";
import { WORKSPACE } from "../constants/testIds";

const SHIPPING_LINES = [
  "MSC",
  "Maersk",
  "CMA CGM",
  "Hapag-Lloyd",
  "ONE",
  "Evergreen",
  "Other",
];
const CONTAINER_TYPES = ["20 GP", "40 GP", "40 HC", "LCL"];
const STATUSES = [
  "Shipment Planning",
  "Carrier Selected",
  "Booking Confirmed",
  "Container Assigned",
  "Ready for Dispatch",
  "Cancelled",
];

export default function FreightIQDetail() {
  const { shipmentId } = useParams();
  const navigate = useNavigate();
  const [ship, setShip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const isAdmin = !!localStorage.getItem("demaxtore_admin_token");

  const [pickup, setPickup] = useState("");
  const [pol, setPol] = useState("");
  const [pod, setPod] = useState("");
  const [finalDest, setFinalDest] = useState("");
  const [incoterm, setIncoterm] = useState("");
  const [cargoReady, setCargoReady] = useState("");
  const [etd, setEtd] = useState("");
  const [eta, setEta] = useState("");
  const [shippingLine, setShippingLine] = useState("Maersk");
  const [serviceType, setServiceType] = useState("");
  const [containerType, setContainerType] = useState("40 HC");
  const [containerQty, setContainerQty] = useState("1");
  const [transitTime, setTransitTime] = useState("");
  const [freightCost, setFreightCost] = useState("");
  const [serviceFee, setServiceFee] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [bookingRef, setBookingRef] = useState("");
  const [containerNumber, setContainerNumber] = useState("");
  const [sealNumber, setSealNumber] = useState("");
  const [opsNotes, setOpsNotes] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const data = await getFreightiq(shipmentId);
      setShip(data);
      setPickup(data.factory_pickup_address || "");
      setPol(data.port_of_loading || "");
      setPod(data.port_of_discharge || "");
      setFinalDest(data.final_destination || "");
      setIncoterm(data.incoterm || "");
      setCargoReady(data.cargo_ready_date || "");
      setEtd(data.etd || "");
      setEta(data.eta || "");
      setShippingLine(data.shipping_line || data.carrier || "Maersk");
      setServiceType(data.service_type || "");
      setContainerType(data.container_type || "40 HC");
      setContainerQty(String(data.container_quantity ?? 1));
      setTransitTime(data.transit_time || "");
      setFreightCost(data.freight_cost != null ? String(data.freight_cost) : "");
      setServiceFee(data.service_fee != null ? String(data.service_fee) : "");
      setCurrency(data.currency || "USD");
      setBookingRef(data.booking_reference || "");
      setContainerNumber(data.container_number || "");
      setSealNumber(data.seal_number || "");
      setOpsNotes(data.ops_notes || "");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load shipment");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipmentId]);

  const sendMsg = async () => {
    if (!msg.trim()) return;
    try {
      await sendFreightiqMessage(shipmentId, msg.trim());
      toast.success("Message sent");
      setMsg("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send message");
    }
  };

  const downloadSummary = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center">
        <Loader2 className="w-6 h-6 animate-spin text-[hsl(217,90%,25%)]" />
      </div>
    );
  }
  if (!ship) {
    return (
      <div className="min-h-screen grid place-items-center text-neutral-500">
        Shipment not found.
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(60,5%,98%)]" data-testid={WORKSPACE.freightiqDetail}>
      <header className="border-b border-neutral-200 bg-white sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/workspace/freightiq" className="inline-flex items-center gap-3">
            <span className="w-8 h-8 rounded-sm bg-[hsl(217,90%,25%)] grid place-items-center">
              <span className="text-white font-bold text-sm">D</span>
            </span>
            <span className="font-medium tracking-tight text-lg">DeMaxtore</span>
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-neutral-400 ml-4 hidden sm:inline">
              FreightIQ™️
            </span>
          </Link>
          <Button variant="outline" className="rounded-sm h-10" onClick={downloadSummary}>
            Download Freight Summary
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10 space-y-10">
        <button
          type="button"
          onClick={() => navigate("/workspace/freightiq")}
          className="inline-flex items-center text-sm text-neutral-600 hover:text-neutral-900"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> All shipments
        </button>

        {/* Section 1 — Summary */}
        <section className="border border-neutral-200 bg-white rounded-sm p-6 lg:p-8">
          <div className="text-xs uppercase tracking-[0.24em] font-semibold text-[hsl(217,90%,35%)]">
            Shipment Summary
          </div>
          <h1 className="mt-3 text-3xl font-light tracking-tight font-mono text-[hsl(217,90%,25%)]">
            {ship.number}
          </h1>
          <div className="mt-4 flex flex-wrap gap-2">
            <Badge className="rounded-sm bg-[hsl(217,90%,25%)] text-white uppercase text-[10px] tracking-wider">
              {ship.status}
            </Badge>
            {ship.eta && (
              <Badge variant="outline" className="rounded-sm uppercase text-[10px] tracking-wider">
                ETA {ship.eta}
              </Badge>
            )}
          </div>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <Meta
              label="Purchase Order"
              value={
                ship.po_id ? (
                  <Link
                    to={`/workspace/purchase-orders/${ship.po_id}`}
                    className="text-[hsl(217,90%,25%)] hover:underline font-mono"
                  >
                    {ship.po_number}
                  </Link>
                ) : (
                  "—"
                )
              }
            />
            <Meta label="Buyer" value={ship.buyer?.company || ship.buyer?.name || "—"} />
            <Meta label="Manufacturer" value={ship.manufacturer?.name} />
            <Meta label="Origin" value={ship.origin || "—"} />
            <Meta label="Destination" value={ship.destination || "—"} />
            <Meta
              label="Inspection"
              value={
                ship.inspection_id ? (
                  <Link
                    to={`/workspace/inspections/${ship.inspection_id}`}
                    className="text-[hsl(217,90%,25%)] hover:underline font-mono"
                  >
                    {ship.inspection_number}
                  </Link>
                ) : (
                  "—"
                )
              }
            />
            <Meta
              label="Created"
              value={ship.created_at ? new Date(ship.created_at).toLocaleString() : "—"}
            />
            <Meta label="Carrier" value={ship.carrier || ship.shipping_line || "—"} />
          </div>
        </section>

        {/* Section 2 — Planning */}
        <section>
          <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-neutral-500 mb-3">
            Shipment Planning
          </h2>
          <div className="border border-neutral-200 bg-white rounded-sm p-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
            <Meta label="Factory Pickup Address" value={ship.factory_pickup_address || "—"} />
            <Meta label="Port of Loading" value={ship.port_of_loading || "—"} />
            <Meta label="Port of Discharge" value={ship.port_of_discharge || "—"} />
            <Meta label="Final Destination" value={ship.final_destination || "—"} />
            <Meta label="Incoterm" value={ship.incoterm || "—"} />
            <Meta label="Cargo Ready Date" value={ship.cargo_ready_date || "—"} />
            <Meta label="Estimated Departure (ETD)" value={ship.etd || "—"} />
            <Meta label="Estimated Arrival (ETA)" value={ship.eta || "—"} />
          </div>
        </section>

        {/* Section 3 — Carrier */}
        <section>
          <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-neutral-500 mb-3">
            Carrier Information
          </h2>
          <div className="border border-neutral-200 bg-white rounded-sm p-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
            <Meta label="Shipping Line" value={ship.shipping_line || "—"} />
            <Meta label="Service Type" value={ship.service_type || "—"} />
            <Meta label="Container Type" value={ship.container_type || "—"} />
            <Meta label="Number of Containers" value={ship.container_quantity ?? "—"} />
            <Meta label="Transit Time" value={ship.transit_time || "—"} />
          </div>
        </section>

        {/* Section 4 — Freight */}
        <section>
          <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-neutral-500 mb-3">
            Freight Information
          </h2>
          <div className="border border-neutral-200 bg-white rounded-sm p-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
            <Meta
              label="Freight Cost"
              value={
                ship.freight_cost != null ? `${ship.freight_cost} ${ship.currency}` : "—"
              }
            />
            <Meta
              label="Service Fee"
              value={
                ship.service_fee != null ? `${ship.service_fee} ${ship.currency}` : "—"
              }
            />
            <Meta
              label="Total Logistics Cost"
              value={
                ship.total_logistics_cost != null
                  ? `${ship.total_logistics_cost} ${ship.currency}`
                  : "—"
              }
            />
            <Meta label="Booking Reference" value={ship.booking_reference || "—"} />
            <Meta label="Container Number" value={ship.container_number || "—"} />
            <Meta label="Seal Number" value={ship.seal_number || "—"} />
          </div>
        </section>

        {/* Section 5 — Milestones */}
        <section data-testid={WORKSPACE.freightiqMilestones}>
          <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-neutral-500 mb-3">
            Shipment Milestones
          </h2>
          <ol className="border border-neutral-200 bg-white rounded-sm divide-y divide-neutral-100">
            {(ship.milestones || []).map((m) => (
              <li key={m.key} className="p-4 flex items-center gap-3 text-sm">
                <span
                  className={`w-2.5 h-2.5 rounded-full ${
                    m.status === "done" ? "bg-emerald-600" : "bg-neutral-300"
                  }`}
                />
                <span className="font-medium flex-1">{m.label}</span>
                <span className="text-neutral-400 text-xs">
                  {m.at ? new Date(m.at).toLocaleString() : "Pending"}
                </span>
              </li>
            ))}
          </ol>
        </section>

        {/* Section 6 — Timeline */}
        <section data-testid={WORKSPACE.freightiqTimeline}>
          <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-neutral-500 mb-3">
            Timeline
          </h2>
          <ol className="border border-neutral-200 bg-white rounded-sm divide-y divide-neutral-100">
            {(ship.timeline || []).map((t) => (
              <li key={t.key} className="p-4 flex items-center gap-3 text-sm">
                <span
                  className={`w-2.5 h-2.5 rounded-full ${
                    t.status === "done" ? "bg-emerald-600" : "bg-neutral-300"
                  }`}
                />
                <span className="font-medium flex-1">{t.label}</span>
                <span className="text-neutral-400 text-xs">
                  {t.at ? new Date(t.at).toLocaleString() : "Pending"}
                </span>
              </li>
            ))}
          </ol>
        </section>

        {/* Message */}
        <section>
          <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-neutral-500 mb-3">
            Send Message
          </h2>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              value={msg}
              onChange={(e) => setMsg(e.target.value)}
              placeholder="Message operations about this shipment…"
              className="h-11 rounded-sm flex-1"
            />
            <Button variant="outline" className="rounded-sm h-11" onClick={sendMsg}>
              <MessageSquare className="w-4 h-4 mr-2" /> Send
            </Button>
          </div>
        </section>

        {/* Ops panel */}
        {isAdmin && (
          <section
            data-testid={WORKSPACE.freightiqOpsPanel}
            className="border border-[hsl(217,90%,25%)]/20 bg-white rounded-sm p-6 space-y-6"
          >
            <h2 className="text-xs uppercase tracking-[0.2em] font-semibold text-[hsl(217,90%,25%)]">
              Operations
            </h2>

            <div className="grid sm:grid-cols-2 gap-4">
              <label className="block text-sm sm:col-span-2">
                <span className="text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500">
                  Factory pickup address
                </span>
                <Input
                  value={pickup}
                  onChange={(e) => setPickup(e.target.value)}
                  className="mt-2 h-11 rounded-sm"
                />
              </label>
              <label className="block text-sm">
                <span className="text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500">
                  Port of loading
                </span>
                <Input value={pol} onChange={(e) => setPol(e.target.value)} className="mt-2 h-11 rounded-sm" />
              </label>
              <label className="block text-sm">
                <span className="text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500">
                  Port of discharge
                </span>
                <Input value={pod} onChange={(e) => setPod(e.target.value)} className="mt-2 h-11 rounded-sm" />
              </label>
              <label className="block text-sm">
                <span className="text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500">
                  Final destination
                </span>
                <Input
                  value={finalDest}
                  onChange={(e) => setFinalDest(e.target.value)}
                  className="mt-2 h-11 rounded-sm"
                />
              </label>
              <label className="block text-sm">
                <span className="text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500">
                  Incoterm
                </span>
                <Input
                  value={incoterm}
                  onChange={(e) => setIncoterm(e.target.value)}
                  className="mt-2 h-11 rounded-sm"
                />
              </label>
              <label className="block text-sm">
                <span className="text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500">
                  Cargo ready date
                </span>
                <Input
                  type="date"
                  value={cargoReady}
                  onChange={(e) => setCargoReady(e.target.value)}
                  className="mt-2 h-11 rounded-sm"
                />
              </label>
              <label className="block text-sm">
                <span className="text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500">
                  ETD
                </span>
                <Input
                  type="date"
                  value={etd}
                  onChange={(e) => setEtd(e.target.value)}
                  className="mt-2 h-11 rounded-sm"
                />
              </label>
              <label className="block text-sm">
                <span className="text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500">
                  ETA
                </span>
                <Input
                  type="date"
                  value={eta}
                  onChange={(e) => setEta(e.target.value)}
                  className="mt-2 h-11 rounded-sm"
                />
              </label>

              <label className="block text-sm">
                <span className="text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500">
                  Shipping line
                </span>
                <select
                  value={shippingLine}
                  onChange={(e) => setShippingLine(e.target.value)}
                  className="mt-2 h-11 w-full rounded-sm border border-neutral-200 px-3 text-sm bg-white"
                >
                  {SHIPPING_LINES.map((l) => (
                    <option key={l} value={l}>
                      {l}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-sm">
                <span className="text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500">
                  Service type
                </span>
                <Input
                  value={serviceType}
                  onChange={(e) => setServiceType(e.target.value)}
                  placeholder="e.g. FCL Direct"
                  className="mt-2 h-11 rounded-sm"
                />
              </label>
              <label className="block text-sm">
                <span className="text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500">
                  Container type
                </span>
                <select
                  value={containerType}
                  onChange={(e) => setContainerType(e.target.value)}
                  className="mt-2 h-11 w-full rounded-sm border border-neutral-200 px-3 text-sm bg-white"
                >
                  {CONTAINER_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-sm">
                <span className="text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500">
                  Containers
                </span>
                <Input
                  type="number"
                  min="1"
                  value={containerQty}
                  onChange={(e) => setContainerQty(e.target.value)}
                  className="mt-2 h-11 rounded-sm"
                />
              </label>
              <label className="block text-sm">
                <span className="text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500">
                  Transit time
                </span>
                <Input
                  value={transitTime}
                  onChange={(e) => setTransitTime(e.target.value)}
                  placeholder="e.g. 28 days"
                  className="mt-2 h-11 rounded-sm"
                />
              </label>
              <label className="block text-sm">
                <span className="text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500">
                  Freight cost
                </span>
                <Input
                  type="number"
                  value={freightCost}
                  onChange={(e) => setFreightCost(e.target.value)}
                  className="mt-2 h-11 rounded-sm"
                />
              </label>
              <label className="block text-sm">
                <span className="text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500">
                  Service fee
                </span>
                <Input
                  type="number"
                  value={serviceFee}
                  onChange={(e) => setServiceFee(e.target.value)}
                  className="mt-2 h-11 rounded-sm"
                />
              </label>
              <label className="block text-sm">
                <span className="text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500">
                  Currency
                </span>
                <Input
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  className="mt-2 h-11 rounded-sm"
                />
              </label>
              <label className="block text-sm">
                <span className="text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500">
                  Booking reference
                </span>
                <Input
                  value={bookingRef}
                  onChange={(e) => setBookingRef(e.target.value)}
                  className="mt-2 h-11 rounded-sm"
                />
              </label>
              <label className="block text-sm">
                <span className="text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500">
                  Container number
                </span>
                <Input
                  value={containerNumber}
                  onChange={(e) => setContainerNumber(e.target.value)}
                  className="mt-2 h-11 rounded-sm"
                />
              </label>
              <label className="block text-sm">
                <span className="text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500">
                  Seal number
                </span>
                <Input
                  value={sealNumber}
                  onChange={(e) => setSealNumber(e.target.value)}
                  className="mt-2 h-11 rounded-sm"
                />
              </label>
              <label className="block text-sm sm:col-span-2">
                <span className="text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500">
                  Ops notes
                </span>
                <Textarea
                  value={opsNotes}
                  onChange={(e) => setOpsNotes(e.target.value)}
                  className="mt-2 min-h-[70px] rounded-sm"
                />
              </label>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                disabled={busy}
                variant="outline"
                className="rounded-sm h-10"
                onClick={async () => {
                  setBusy(true);
                  try {
                    await updateFreightiq(shipmentId, {
                      factory_pickup_address: pickup || null,
                      port_of_loading: pol || null,
                      port_of_discharge: pod || null,
                      final_destination: finalDest || null,
                      incoterm: incoterm || null,
                      cargo_ready_date: cargoReady || null,
                      etd: etd || null,
                      eta: eta || null,
                      freight_cost: freightCost !== "" ? Number(freightCost) : null,
                      service_fee: serviceFee !== "" ? Number(serviceFee) : null,
                      currency: currency || "USD",
                      ops_notes: opsNotes || null,
                      transit_time: transitTime || null,
                      service_type: serviceType || null,
                    });
                    toast.success("Shipment plan saved");
                    await load();
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || "Update failed");
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                Save planning & costs
              </Button>

              <Button
                disabled={busy}
                className="rounded-sm h-10 bg-[hsl(217,90%,25%)] text-white"
                onClick={async () => {
                  setBusy(true);
                  try {
                    await selectFreightCarrier(shipmentId, {
                      shipping_line: shippingLine,
                      service_type: serviceType || null,
                      container_type: containerType || null,
                      container_quantity: containerQty ? Number(containerQty) : null,
                      transit_time: transitTime || null,
                    });
                    toast.success("Carrier selected");
                    await load();
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || "Carrier selection failed");
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                Select Carrier
              </Button>

              <Button
                disabled={busy || !bookingRef.trim()}
                className="rounded-sm h-10"
                onClick={async () => {
                  setBusy(true);
                  try {
                    await saveFreightBooking(shipmentId, {
                      booking_reference: bookingRef.trim(),
                      freight_cost: freightCost !== "" ? Number(freightCost) : null,
                      service_fee: serviceFee !== "" ? Number(serviceFee) : null,
                      currency: currency || null,
                      etd: etd || null,
                      eta: eta || null,
                    });
                    toast.success("Booking saved");
                    await load();
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || "Booking failed");
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                Save Booking
              </Button>

              <Button
                disabled={busy || !containerNumber.trim()}
                className="rounded-sm h-10"
                onClick={async () => {
                  setBusy(true);
                  try {
                    await assignFreightContainer(shipmentId, {
                      container_number: containerNumber.trim(),
                      seal_number: sealNumber || null,
                      container_type: containerType || null,
                      container_quantity: containerQty ? Number(containerQty) : null,
                    });
                    toast.success("Container assigned");
                    await load();
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || "Container assign failed");
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                Assign Container
              </Button>

              <Button
                disabled={busy || !eta}
                variant="outline"
                className="rounded-sm h-10"
                onClick={async () => {
                  setBusy(true);
                  try {
                    await updateFreightEta(shipmentId, { eta, etd: etd || null });
                    toast.success("ETA updated");
                    await load();
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || "ETA update failed");
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                Update ETA
              </Button>

              <Button
                disabled={busy}
                className="rounded-sm h-10 bg-emerald-700 text-white"
                onClick={async () => {
                  setBusy(true);
                  try {
                    await updateFreightStatus(shipmentId, { status: "Ready for Dispatch" });
                    toast.success("Ready for dispatch — live tracking created");
                    await load();
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || "Status update failed");
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                Ready for Dispatch
              </Button>
            </div>

            <div className="flex flex-wrap gap-2 pt-2 border-t border-neutral-100">
              <span className="text-xs uppercase tracking-[0.15em] font-semibold text-neutral-500 w-full mb-1">
                Set status
              </span>
              {STATUSES.filter((s) => s !== "Cancelled").map((status) => (
                <Button
                  key={status}
                  disabled={busy || ship.status === status}
                  variant="outline"
                  className="rounded-sm h-9 text-xs"
                  onClick={async () => {
                    setBusy(true);
                    try {
                      await updateFreightStatus(shipmentId, { status });
                      toast.success(`Status: ${status}`);
                      await load();
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || "Status failed");
                    } finally {
                      setBusy(false);
                    }
                  }}
                >
                  {status}
                </Button>
              ))}
            </div>
          </section>
        )}

        <div className="flex flex-wrap gap-4 text-sm">
          {ship.po_id && (
            <Link
              to={`/workspace/purchase-orders/${ship.po_id}`}
              className="text-[hsl(217,90%,25%)] hover:underline"
            >
              ← Purchase Order {ship.po_number}
            </Link>
          )}
          {ship.inspection_id && (
            <Link
              to={`/workspace/inspections/${ship.inspection_id}`}
              className="text-[hsl(217,90%,25%)] hover:underline"
            >
              ← Inspection {ship.inspection_number}
            </Link>
          )}
          {(ship.tracking_id || ship.status === "Ready for Dispatch") && (
            <Link
              to={
                ship.tracking_id
                  ? `/workspace/shipments/${ship.tracking_id}`
                  : `/workspace/shipments?po_id=${ship.po_id}`
              }
              className="text-[hsl(217,90%,25%)] hover:underline"
            >
              Live Tracking {ship.tracking_number || ""} →
            </Link>
          )}
        </div>
      </main>
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.15em] text-neutral-400 font-semibold">
        {label}
      </div>
      <div className="mt-1 text-neutral-800 break-words">{value ?? "—"}</div>
    </div>
  );
}
